import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, CalendarBlank, CalendarPlus, Check, Clock, MapPin, ShareNetwork, Warning } from 'phosphor-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { GoldenRule } from '@/components/ui/GoldenRule';
import { Monogram } from '@/components/ui/Monogram';
import { FallbackPoster } from '@/components/ui/FallbackPoster';
import { EditionBadge } from '@/components/ui/EditionBadge';
import { TamponJyEtais } from '@/components/ui/TamponJyEtais';
import { SourceFooter } from '@/components/ui/SourceFooter';
import { EventCard } from '@/components/EventCard';
import { categoryOf, AUDIENCE_LABEL } from '@/constants/categories';
import { formatDayLong, formatTime, formatRange, hasKnownTime } from '@/lib/dates';
import { upperCity } from '@/lib/city';
import { getResponsiveImageProps } from '@/lib/images';
import type { EventRow, OrganizerRow } from '@/types/database';
import { useAuth } from '@/hooks/useAuth';
import { useFavoritesIds } from '@/hooks/useFavorites';
import { hap } from '@/lib/haptics';
import { Capacitor } from '@capacitor/core';
import { openExternal } from '@/lib/native';
import { ShareEventModal } from '@/components/share/ShareEventModal';
import { requestStartupNotifications } from '@/lib/appRuntime';
import { syncPushTokenForUser } from '@/lib/pushNotifications';
import { Skeleton } from '@/components/ui/Skeleton';
import { EventMap } from '@/components/EventMap';
import { AdBannerDetail } from '@/components/ads/AdBannerDetail';

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { user } = useAuth();
  const fav = useFavoritesIds();
  const [event, setEvent] = useState<EventRow | null>(null);
  const [org, setOrg] = useState<OrganizerRow | null>(null);
  const [also, setAlso] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [agendaNote, setAgendaNote] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      if (!id) return;
      setLoading(true);
      const { data: ev } = await supabase.from('events').select('*').eq('id', id).maybeSingle();
      if (!alive || !ev) { setLoading(false); return; }
      setEvent(ev as EventRow);
      if (ev.organizer_id) {
        const { data: o } = await supabase.from('organizers').select('*').eq('id', ev.organizer_id).maybeSingle();
        setOrg(o as OrganizerRow);
      }
      const { data: alsoData } = await supabase.from('events').select('*')
        .eq('kind', ev.kind).neq('id', ev.id).gte('starts_at', new Date().toISOString())
        .order('starts_at').limit(3);
      setAlso((alsoData as EventRow[]) ?? []);
      setLoading(false);
    }
    load();
    return () => { alive = false; };
  }, [id, user?.id]);

  function share() {
    if (!event) return;
    // Ouvre l'aperçu de l'image partageable : on ne poste pas un visuel généré
    // (au fond aléatoire) sans l'avoir vu.
    hap.light();
    setShareOpen(true);
  }
  async function toggleAgenda() {
    if (!event) return;
    hap.light();
    const added = await fav.toggle(event.id); // favoris locaux en invité, en base si connecté
    if (added) {
      // C'est LE moment où la permission de notifier a du sens : on la demande
      // ici (natif), pas à froid au premier lancement.
      if (user) {
        setAgendaNote('Vous serez prévenu·e la veille par notification.');
        if (Capacitor.isNativePlatform()) {
          void requestStartupNotifications(async (token) => {
            await syncPushTokenForUser(user.id, token);
          }).catch(() => {});
        }
      } else {
        setAgendaNote('Connectez-vous pour recevoir un rappel la veille.');
      }
      window.setTimeout(() => setAgendaNote(null), 5000);
    } else {
      setAgendaNote(null);
    }
  }
  async function report() {
    if (!event) return;
    if (!confirm('Signaler cet événement comme inapproprié ?')) return;
    await supabase.from('reports').insert({ event_id: event.id, user_id: user?.id ?? null, reason: 'user_report' });
    alert('Merci, signalement enregistré.');
  }

  if (loading) {
    return <div className="min-h-screen bg-paper">
      <Skeleton className="h-[60vh] w-full" />
      <div className="px-5 py-6 space-y-3"><Skeleton className="h-6 w-1/2" /><Skeleton className="h-10 w-full" /><Skeleton className="h-4 w-3/4" /></div>
    </div>;
  }
  if (!event) {
    return <div className="min-h-screen px-6 py-10 text-center"><p className="t-ed-lg">Événement introuvable.</p>
      <Button className="mt-4" onClick={() => nav('/decouvrir')}>Retour</Button></div>;
  }

  const meta = categoryOf(event.kind);
  const price = event.is_free ? 'Gratuit'
    : event.price_min != null ? (event.price_max && event.price_max > event.price_min ? `${event.price_min}–${event.price_max} €` : `${event.price_min} €`)
    : null;
  const isFav = fav.ids.has(event.id);
  // Uniquement le vrai site officiel — plus de bouton « Voir la source ».
  const primaryLink = event.url ?? null;
  const heroImage = getResponsiveImageProps(event.image_url, {
    widths: [480, 768, 960, 1280, 1600],
    sizes: '100vw',
    fallbackWidth: 960,
    quality: 72,
  });

  return (
    <div className="min-h-screen bg-paper text-ink-1 pb-32">
      {/* Hero */}
      <div className="relative">
        {event.image_url
          ? <img src={heroImage?.src ?? event.image_url} srcSet={heroImage?.srcSet} sizes={heroImage?.sizes} alt="" className="w-full h-[60vh] object-cover" fetchPriority="high" decoding="async" />
          : <FallbackPoster kind={event.kind} title={event.title} city={event.city} startsAt={event.starts_at} ratio="portrait" className="!aspect-auto h-[60vh]" />}
        <div className="absolute top-[max(env(safe-area-inset-top),12px)] left-3 right-3 flex justify-between items-start">
          <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full grid place-items-center bg-paper-elev/90 backdrop-blur ring-1 ring-paper-edge"><ArrowLeft size={18} /></button>
          <button onClick={share} aria-label="Partager" className="h-10 w-10 rounded-full grid place-items-center bg-paper-elev/90 backdrop-blur ring-1 ring-paper-edge"><ShareNetwork size={18} /></button>
        </div>
      </div>

      <div className="px-5 -mt-6 relative">
        <EditionBadge seed={event.id} className="text-paper-elev/95 drop-shadow" />
      </div>

      <div className="px-5 pt-6">
        <div className="t-cap mb-2">{meta.label}{event.audiences && event.audiences.length > 0 && <> · {event.audiences.map(a => AUDIENCE_LABEL[a]).join(' · ')}</>}</div>
        <h1 className="t-ed-3xl mb-4">{event.title}</h1>
        <Button
          variant={isFav ? 'outline' : 'secondary'}
          full
          iconLeft={isFav ? <Check size={18} weight="bold" /> : <CalendarPlus size={18} />}
          className="mb-4"
          onClick={toggleAgenda}
        >
          {isFav ? 'Ajouté à mon agenda' : 'Ajouter à mon agenda'}
        </Button>
        {agendaNote ? <p className="t-ui-xs text-ink-3 mt-2 text-center">{agendaNote}</p> : null}
        <GoldenRule short />
        <div className="mt-5 space-y-2.5 text-ink-2">
          <div className="flex items-center gap-3"><CalendarBlank size={18} className="text-ink-3" /><span className="t-mono-sm">{formatRange(event.starts_at, event.ends_at).toUpperCase()}</span></div>
          {hasKnownTime(event.starts_at) && (
            <div className="flex items-center gap-3"><Clock size={18} className="text-ink-3" /><span className="t-mono-sm">{formatTime(event.starts_at)}{event.ends_at && hasKnownTime(event.ends_at) && ` – ${formatTime(event.ends_at)}`}</span></div>
          )}
          {(event.city || event.lat) && (
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-ink-3 mt-0.5" />
              <div className="flex-1">
                <div className="t-ui">{upperCity(event.city) ?? '—'}</div>
                {event.lat && event.lon && (
                  <button type="button"
                    onClick={() => void openExternal(`https://www.openstreetmap.org/?mlat=${event.lat}&mlon=${event.lon}#map=16/${event.lat}/${event.lon}`)}
                    className="inline-flex items-center gap-1 mt-1 font-serif italic text-[14px] text-terre">
                    Itinéraire <ArrowUpRight size={14} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {event.lat && event.lon && (
          <div className="mt-5 rounded-md overflow-hidden ring-1 ring-paper-edge">
            <EventMap
              events={[event as EventRow]}
              center={{ lat: event.lat, lon: event.lon }}
              height={140}
              interactive={false}
              className="pointer-events-none"
            />
          </div>
        )}

        {event.description && (
          <>
            <div className="mt-8 mb-3 flex items-center gap-3"><h2 className="t-ed-lg whitespace-nowrap">À propos</h2><GoldenRule /></div>
            <p className="t-ui text-ink-2 whitespace-pre-line">{event.description}</p>
          </>
        )}

        {org && (
          <>
            <div className="mt-10 mb-4 flex items-center gap-3"><h2 className="t-ed-lg whitespace-nowrap">Organisé par</h2><GoldenRule /></div>
            <div className="flex items-center gap-3">
              <Monogram name={org.name} size={48} />
              <div className="flex-1 min-w-0">
                <div className="t-ed-md truncate">{org.name}</div>
                {org.city ? <span className="t-ui-xs text-ink-3">{upperCity(org.city)}</span> : null}
              </div>
            </div>
          </>
        )}

        {/* Pub native — réclame entre la fiche organisateur et les actions */}
        <AdBannerDetail adKey={`detail-${id ?? 'x'}`} />

        <div className="mt-10 flex items-center gap-4">
          <TamponJyEtais />
          <button onClick={report} className="ml-auto inline-flex items-center gap-1.5 t-ui-sm font-serif italic text-ink-3 hover:text-cerise">
            <Warning size={14} /> Une erreur ? <span className="underline">prévenez-nous</span>
          </button>
        </div>

        {also.length > 0 && (
          <>
            <div className="mt-12 mb-4 flex items-center gap-3"><h2 className="t-ed-lg whitespace-nowrap">À voir aussi</h2><GoldenRule /></div>
            <div className="flex gap-4 overflow-x-auto -mx-5 px-5 pb-2 scrollbar-none snap-x snap-mandatory">
              {also.map(e => <div key={e.id} className="snap-start"><EventCard event={e} variant="peek" /></div>)}
            </div>
          </>
        )}

        <div className="mt-10">
          <SourceFooter source={event.source} attribution={event.attribution} license={event.license} lastSyncedAt={event.last_synced_at} />
        </div>

        <div className="mt-4 rounded-md bg-paper-elev ring-1 ring-paper-edge p-4">
          <div className="inline-flex items-center gap-2 t-cap text-ink-3">
            <Warning size={14} />
            À vérifier avant d&apos;y aller
          </div>
          <p className="mt-2 t-ui-sm text-ink-2">
            Les informations de cet événement peuvent évoluer. Avant de vous déplacer, vérifiez les horaires,
            tarifs, conditions d&apos;accès et éventuelles annulations auprès de l&apos;organisateur ou sur la source
            officielle. Gazette référence ces informations à titre indicatif et ne peut garantir l&apos;absence de
            modification par des tiers.
          </p>
        </div>
      </div>

      {/* Sticky bottom CTA */}
      <div className="fixed left-0 right-0 bottom-0 z-30 bg-paper-elev/95 backdrop-blur border-t border-paper-edge"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="px-5 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0 leading-tight">
            {price === 'Gratuit'
              ? <div className="font-serif italic text-[18px] text-terre leading-none">Gratuit</div>
              : price ? <div className="t-mono-sm text-ink-1 leading-none">{price}</div>
              : <div className="t-ui-xs text-ink-3 italic font-serif leading-none">Tarif sur place</div>}
            <div className="t-ui-xs text-ink-3 mt-1 truncate">{formatDayLong(event.starts_at)}</div>
          </div>
          {primaryLink && (
            <button type="button" onClick={() => void openExternal(primaryLink)} className="shrink-0 inline-flex items-center gap-2 h-11 px-4 rounded-md bg-terre text-paper-elev font-medium text-[14px]">
              Site officiel <ArrowUpRight size={16} weight="bold" />
            </button>
          )}
        </div>
      </div>

      <ShareEventModal event={event} open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
}
